import {
  PrimaryControlsStripToolbar,
  useNavToolbarPublisher,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useEffect, useMemo, type ReactNode } from "react";
import { MoreIconButton } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.toolbar._components.more-icon-button";
import { StarIconButton } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.toolbar._components.star-icon-button";
import { ViewToggle } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.toolbar._components.view-toggle";
import {
  isMorphemeCardActionsMenuInTabBar,
  useMnemonicFeedbackSheetLauncher,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.card-actions";
import {
  ToolbarActionsLane,
  ToolbarEmbeddedMoreActions,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPopoverActionButton,
  ToolbarPopoverButton,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import { CardBackButtonContainer } from "@-/dev.improve.haniverse.routes.nav.app._components.card-back-button-container";
import {
  PrimaryControlsStripBackControl,
  usePrimaryControlsStripBackGate,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import {
  GraphemeMorphemePanelsContext,
  MorphemePanelsContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import type { TabBarControls } from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { View, useWindowDimensions } from "@-/com.npmjs.react-native";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";

type MorphemePanelsActionsToolbarProps =
  | (TabBarControls & {
      /**
       * The Learn page shows the card actions to the right of its native reading tabs (iOS 26+), so
       * the header drops its More menu on both pages — Details included — and the Learn tab bar is
       * the one place card actions live.
       */
      cardActionsInLearnTabBar?: boolean;
      floating?: boolean;
      graphemeRoutePrefix: string;
      isPanelSwitchingEnabled?: boolean;
      onBack?: () => void;
      textGlass?: boolean;
    })
  | {
      activePanel: "info" | "mnemonics";
      floating?: boolean;
      onBack?: () => void;
      readingActions?: ReactNode;
      readingTabBar?: ReactNode;
    };

export function MorphemePanelsActionsToolbar(props: MorphemePanelsActionsToolbarProps) {
  const { onBack } = props;
  const tabBarProps = "state" in props ? props : undefined;
  const { diameter, height } = useToolbarGridMetrics();
  const { width } = useWindowDimensions();
  const readingTabBar = "readingTabBar" in props ? props.readingTabBar : undefined;
  const readingActions = "readingActions" in props ? props.readingActions : undefined;
  const hasReadingTabs = readingTabBar !== undefined;
  const textGlass = hasReadingTabs || (tabBarProps?.textGlass ?? false);
  const publish = useNavToolbarPublisher();
  const isFocused = useIsFocused();
  // The slots render in the nav toolbar host, outside this subtree's providers, so they carry
  // both panel contexts along.
  const [panelContext] = useContextSelectors(MorphemePanelsContext, (c) => c);
  const [graphemePanelContext] = useContextSelectors(GraphemeMorphemePanelsContext, (c) => c);
  const backGate = usePrimaryControlsStripBackGate({ appShellPlacement: "toolbar" });
  const [morpheme, catalogMnemonic, ownMnemonic] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.mnemonic?.trim() ?? "",
    (c) => c.userSlice.data.meaningCard?.description?.trim() ?? "",
  );
  const [toolbarEditAction] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.toolbarEditAction,
  );
  // Mirror what the panel displays: a reader's own mnemonic wins over the catalog one, so
  // feedback is attributed to the text they were actually looking at.
  const currentMnemonic = ownMnemonic.length > 0 ? ownMnemonic : catalogMnemonic;
  // Give Feedback lives in the More menu, whose content unmounts as the menu closes on the press,
  // so the sheet it presents is owned here and rendered in the page's own tree.
  const feedbackSheet = useMnemonicFeedbackSheetLauncher({ character: morpheme, currentMnemonic });
  const openFeedbackSheet = feedbackSheet.open;
  const activeRouteName =
    "activePanel" in props ? props.activePanel : props.state.routes[props.state.index]?.name;
  const visibleToolbarEditAction = activeRouteName === "mnemonics" ? toolbarEditAction : null;
  const areActionsInLearnTabBar =
    (tabBarProps?.cardActionsInLearnTabBar ?? false) && isMorphemeCardActionsMenuInTabBar;

  // A morpheme's panels are always a nested page, so its header slot holds the back button and
  // its actions live in a More menu embedded beside the panel tabs instead.
  const actions = useMemo(
    () => (
      <ToolbarEmbeddedMoreActions>
        <MorphemePanelsContext.Provider value={panelContext}>
          <GraphemeMorphemePanelsContext.Provider value={graphemePanelContext}>
            {visibleToolbarEditAction === null ? null : (
              // Like every tray pill this opens a popover; editing (or finishing) is its one action.
              <ToolbarPopoverButton
                accessibilityLabel={
                  visibleToolbarEditAction.isEditing ? "Finish editing flashcard" : "Edit flashcard"
                }
                iconSize={16}
                label={visibleToolbarEditAction.isEditing ? "Done" : "Edit"}
                name={visibleToolbarEditAction.isEditing ? "LuCheck" : "LuPenLine"}
              >
                <ToolbarPopoverActionButton
                  label={
                    visibleToolbarEditAction.isEditing
                      ? "Finish editing flashcard"
                      : "Edit flashcard"
                  }
                  name={visibleToolbarEditAction.isEditing ? "LuCheck" : "LuPenLine"}
                  onPress={visibleToolbarEditAction.onPress}
                  testID="morpheme-mnemonic-edit-action"
                />
              </ToolbarPopoverButton>
            )}
            <StarIconButton />
            <MoreIconButton character={morpheme} onGiveFeedback={openFeedbackSheet} />
            {/*<AddToCollectionIconButton />*/}
          </GraphemeMorphemePanelsContext.Provider>
        </MorphemePanelsContext.Provider>
      </ToolbarEmbeddedMoreActions>
    ),
    [graphemePanelContext, panelContext, visibleToolbarEditAction, morpheme, openFeedbackSheet],
  );

  const slots = useMemo(
    () => ({
      actions: null,
      leading:
        backGate !== null ? (
          <PrimaryControlsStripBackControl gate={backGate} layout="app-shell" />
        ) : onBack === undefined ? null : (
          <CardBackButtonContainer inset="none" onBack={onBack} />
        ),
      panels: hasReadingTabs ? (
        <MorphemePanelsContext.Provider value={panelContext}>
          <GraphemeMorphemePanelsContext.Provider value={graphemePanelContext}>
            <View pointerEvents="box-none" style={{ width: width - 2 * (diameter + 18) }}>
              {readingTabBar}
            </View>
          </GraphemeMorphemePanelsContext.Provider>
        </MorphemePanelsContext.Provider>
      ) : tabBarProps ? (
        // The leading spacer balances the menu so the tabs stay centred.
        <View className="flex-row items-center gap-1" pointerEvents="box-none">
          {areActionsInLearnTabBar ? null : (
            <View pointerEvents="none" style={{ width: diameter }} />
          )}
          <ViewToggle
            {...tabBarProps}
            grapheme={morpheme}
            graphemeRoutePrefix={tabBarProps.graphemeRoutePrefix}
            isPanelSwitchingEnabled={tabBarProps.isPanelSwitchingEnabled ?? true}
          />
          {areActionsInLearnTabBar ? null : actions}
        </View>
      ) : (
        actions
      ),
    }),
    [
      actions,
      areActionsInLearnTabBar,
      backGate,
      diameter,
      graphemePanelContext,
      hasReadingTabs,
      morpheme,
      onBack,
      panelContext,
      readingTabBar,
      tabBarProps,
      width,
    ],
  );

  useEffect(() => {
    if (publish === null || !isFocused) {
      return;
    }
    return publish(slots);
  }, [publish, isFocused, slots]);

  // Where Details carries the card actions beside its own bottom tabs (iOS 26, Android), no menu
  // floats over the reading sheets beneath the drawer button. Elsewhere that floating menu is the
  // card actions' only home: it stays in the page-sized touch surface, outside the shorter toolbar
  // host, its circle aligned with the drawer above it on every reading.
  const sheetActionsTop = publish === null ? height : 0;
  const sheetActions =
    hasReadingTabs && !isMorphemeCardActionsMenuInTabBar ? (
      <View className="absolute right-2.25 z-20" style={{ top: sheetActionsTop }}>
        {readingActions ?? actions}
      </View>
    ) : null;

  if (publish !== null) {
    // Render-into-host: the slots above are published into the nav toolbar host, so only the
    // feedback sheet renders in place.
    return (
      <>
        {sheetActions}
        {feedbackSheet.sheet}
      </>
    );
  }

  // The shared top bar, continuing the page's own surface rather than the app chrome.
  return (
    <>
      <PrimaryControlsStripToolbar
        className="relative"
        floating={"floating" in props ? props.floating : undefined}
        height={textGlass ? height : 48}
        surface={activeRouteName === "mnemonics" ? "surface" : "page"}
      >
        <ToolbarActionsLane>
          <View
            className="mr-2 shrink-0 justify-center"
            style={textGlass ? { height: diameter } : undefined}
          >
            {slots.leading}
          </View>
        </ToolbarActionsLane>
        <View
          className={
            textGlass && !hasReadingTabs
              ? "absolute inset-0 z-10 flex-row items-start justify-center pt-2.25"
              : "absolute inset-0 z-10 flex-row items-start justify-center"
          }
          pointerEvents="box-none"
        >
          {slots.panels}
        </View>
      </PrimaryControlsStripToolbar>
      {sheetActions}
      {feedbackSheet.sheet}
    </>
  );
}
